const CSRF_META_SELECTOR = 'meta[name="csrf-token"]';
// Query/cookie keys we reapply per visitor after serving cached HTML.
const DEFAULT_AD_KEYS = [
  'msclkid',
  'gclid',
  'gbraid',
  'wbraid',
  'ttclid',
  'tbclid',
  'obclid',
  'oppref',
  'dclid',
  'fbclid',
  'fbp',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content'
];

function parsePayload(rawPayload) {
  if (!rawPayload) return null;

  try {
    return JSON.parse(rawPayload);
  } catch (error) {
    console.error('Failed to parse session bootstrap payload', error);
    return null;
  }
}

function enrichAdTracking(payload) {
  if (!payload) return;

  payload.ad_tracking = payload.ad_tracking || {};
  const searchParams = new URLSearchParams(window.location.search);

  DEFAULT_AD_KEYS.forEach((key) => {
    const value = searchParams.get(key);
    if (value && !payload.ad_tracking[key]) {
      payload.ad_tracking[key] = value;
    }
  });
}

// Merge live tracking data taken from the current URL and cookies into the payload.
function mergeDynamicTrackingFields(target = {}) {
  const merged = { ...target };
  const searchParams = new URLSearchParams(window.location.search);

  DEFAULT_AD_KEYS.forEach((key) => {
    const value = searchParams.get(key);
    if (value) {
      merged[key] = value;
    }
  });

  const fbc = getCookie('_fbc');
  if (fbc) merged.fb_click_id = fbc;

  const fbp = getCookie('_fbp');
  if (fbp) merged.fb_browser_id = fbp;

  merged.path = window.location.pathname;
  merged.url = window.location.href;
  if (!merged.referrer) {
    const referrer = document.referrer;
    if (referrer && referrer !== window.location.href) {
      merged.referrer = referrer;
    }
  }
  if (document.body.dataset.sessionId && !merged.session_id) {
    merged.session_id = document.body.dataset.sessionId;
  }

  return merged;
}

function updateMetaTag(token) {
  if (!token) return;
  const meta = document.querySelector(CSRF_META_SELECTOR);
  if (meta) {
    meta.setAttribute('content', token);
  }
}

function ensureAuthenticityField(form, paramName, token) {
  if (!form || !paramName || !token) return;

  const method = (form.getAttribute('method') || 'get').toLowerCase();
  if (method === 'get') return;

  let input = form.querySelector(`input[name="${paramName}"]`);
  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = paramName;
    form.appendChild(input);
  }
  input.value = token;
}

function updateFormTokens(paramName, token) {
  if (!paramName || !token) return;

  document.querySelectorAll('form').forEach((form) => {
    ensureAuthenticityField(form, paramName, token);
  });
}

// Fetch `/session/bootstrap`, hydrate the session cookie, then log the page view.
async function bootstrapSession() {
  const { body } = document;
  if (!body) return;

  const url = body.dataset.sessionBootstrapUrl;
  const rawPayload = body.dataset.sessionBootstrap;

  if (!url || !rawPayload) return;

  const marker = `${url}|${rawPayload}`;
  if (body.dataset.sessionBootstrapPending === marker || body.dataset.sessionBootstrapRan === marker) {
    return;
  }

  const payload = parsePayload(rawPayload);
  if (!payload || !payload.context) return;

  body.dataset.sessionBootstrapPending = marker;
  delete body.dataset.pageViewTracked;

  enrichAdTracking(payload);

  try {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Bootstrap request failed with status ${response.status}`);
    }

    const data = await response.json();
    updateMetaTag(data.csrf_token);
    updateFormTokens(data.csrf_param, data.csrf_token);

    if (data.session_id) {
      body.dataset.sessionId = data.session_id;
    }

    document.dispatchEvent(new CustomEvent('session:bootstrapped', { detail: data }));

    await trackPageView({
      sessionId: data.session_id,
    });

    body.dataset.sessionBootstrapRan = marker;
  } catch (error) {
    console.error('Session bootstrap failed', error);
    await trackPageView();
    delete body.dataset.sessionBootstrapRan;
  } finally {
    delete body.dataset.sessionBootstrapPending;
  }
}

function getCacheStatusAndVersion() {
  if (!('performance' in window) || typeof performance.getEntriesByType !== 'function') {
    return { cacheStatus: null, cacheVersion: null };
  }

  const [navigationEntry] = performance.getEntriesByType('navigation');
  if (!navigationEntry || !navigationEntry.serverTiming) {
    return { cacheStatus: null, cacheVersion: null };
  }

  let cacheStatus = navigationEntry.serverTiming.find((metric) => metric.name === 'edge-cache');
  cacheStatus = cacheStatus?.description?.toUpperCase() || null;

  let cacheVersion = navigationEntry.serverTiming.find((metric) => metric.name === 'edge-version');
  cacheVersion = cacheVersion?.description || null;

  return { cacheStatus, cacheVersion };
}

async function trackPageView({ sessionId } = {}) {
  const rawPageView = document.body.dataset.pageView;
  if (!rawPageView) return;
  if (document.body.dataset.pageViewTracked === '1') return;

  const { cacheStatus, cacheVersion } = getCacheStatusAndVersion();
  const normalizedStatus = cacheStatus || 'UNKNOWN';
  const normalizedVersion = cacheVersion || 'UNKNOWN';

  try {
    const pageView = JSON.parse(rawPageView);
    if (sessionId) {
      pageView.session_id = sessionId;
    }

    const mergedPageView = mergeDynamicTrackingFields({ ...pageView });
    mergedPageView.cache_status = normalizedStatus;
    mergedPageView.cache_version = normalizedVersion;

    const token = document.querySelector(CSRF_META_SELECTOR)?.getAttribute('content');
    if (!token) {
      console.error('Missing CSRF token for tracking');
      return;
    }

    const response = await fetch('/tracking/page_view', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': token
      },
      body: JSON.stringify({
        page_view: mergedPageView,
        cache_status: normalizedStatus
      })
    });

    if (!response.ok) {
      throw new Error(`Page view tracking failed with status ${response.status}`);
    }

    document.body.dataset.pageViewTracked = '1';
  } catch (error) {
    console.error('Page view tracking failed', error);
  }
}

function getCookie(name) {
  const value = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  return value ? decodeURIComponent(value.split('=')[1]) : null;
}

export function scheduleBootstrap() {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootstrapSession);
  } else {
    bootstrapSession();
  }

  document.addEventListener("turbo:load", bootstrapSession);
}
